import React, { useState } from 'react';
import { Menu, X } from 'lucide-react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';

const NAV_ITEMS = [
  { name: 'Problem', href: '#problem' },
  { name: 'Approach', href: '#approach' },
  { name: 'Technology', href: '#technology' },
];

const NewHeader = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { scrollY } = useScroll();

  const background = useTransform(
    scrollY,
    [0, 120],
    ['rgba(11,17,32,0)', 'rgba(11,17,32,0.82)']
  );
  const border = useTransform(
    scrollY,
    [0, 120],
    ['rgba(255,255,255,0)', 'rgba(255,255,255,0.08)']
  );

  return (
    <motion.header
      style={{ backgroundColor: background, borderColor: border }}
      className="fixed top-0 left-0 right-0 z-50 border-b backdrop-blur-md transition-colors duration-300"
    >
      <div className="container-edge">
        <div className="flex items-center justify-between gap-6 lg:gap-10 h-20">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center gap-3 shrink-0"
          >
            <Link
              to="/"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="group flex items-center gap-3 text-white"
              aria-label="Kinesight — home"
            >
              <img
                src="/kinesight_logo.png"
                alt=""
                aria-hidden="true"
                className="h-7 md:h-8 w-auto select-none drop-shadow-[0_0_12px_rgba(0,212,170,0.25)] transition-transform duration-500 ease-out group-hover:scale-[1.04]"
                draggable={false}
              />
              <span className="font-display text-lg md:text-xl font-bold tracking-tight uppercase">
                Kinesight
              </span>
            </Link>
          </motion.div>

          <nav className="hidden lg:flex items-center gap-10 whitespace-nowrap">
            {NAV_ITEMS.map((item, index) => (
              <motion.a
                key={item.name}
                href={item.href}
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 * index }}
                className="text-[0.72rem] font-mono uppercase tracking-[0.35em] text-white/60 hover:text-teal transition-colors duration-200"
              >
                {item.name}
              </motion.a>
            ))}
          </nav>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="hidden md:block shrink-0"
          >
            <a href="#cta" className="btn-ghost text-sm whitespace-nowrap py-2.5 px-5">
              Talk to us
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 5l7 7-7 7" />
              </svg>
            </a>
          </motion.div>

          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Toggle menu"
              className="text-white/80 hover:text-teal transition-colors"
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden py-6 space-y-4 border-t border-white/10"
          >
            {NAV_ITEMS.map((item) => (
              <a
                key={item.name}
                href={item.href}
                onClick={() => setIsMenuOpen(false)}
                className="block text-[0.72rem] font-mono uppercase tracking-[0.35em] text-white/70 hover:text-teal transition-colors"
              >
                {item.name}
              </a>
            ))}
            <a
              href="#cta"
              onClick={() => setIsMenuOpen(false)}
              className="btn-teal w-full justify-center text-sm"
            >
              Talk to us
            </a>
          </motion.div>
        )}
      </div>
    </motion.header>
  );
};

export default NewHeader;
